import React, { ReactElement, useState } from "react";
import { StepProps } from "../TradeModal";
import TradeTitle from "components/TradeTitle/TradeTitle";
import { useMutationBundle } from "source/hooks";
import { EquityTradeInputs } from "$gql/types.gen";
import { PreviewTrade } from "$gql/mutations/tradier/PreviewTrade.gen";
import { GetBundleForOrderType, TradeOptions, OrderType } from "./TradeTypes";
import { useForm } from "react-hook-form";
import SmallDataTable from "components/SmallDataTable/SmallDataTable";
import { useQueryBundle } from "source/hooks";
import { GetIexQuote } from "$gql/queries/currentPricing/GetIexQuote.gen";
import { formatCurrency } from "@tiicker/util/lib/formats";
import styles from "./TradeStock.module.scss";
import BrokerConnectionMessage from "components/BrokerConnectionMessage/BrokerConnectionMessage";
import DisclaimerText from "components/DisclaimerText/DisclaimerText";
import Button from "components/core/Button/Button";
import classNames from "classnames";
import FormInput from "components/Form/FormInput";
import DropDownSelect from "components/core/Forms/DropDownSelect/DropDownSelect";

export default function TradeStock(props: StepProps): ReactElement {
  const { institutionAccountId, tickerSymbol, iexSymbol } = props.state;
  if (!institutionAccountId) {
    throw new Error("Institution Account is null");
  }

  props.setTitle(`Trade ${props.state.tickerSymbol}`);

  // Pull price here so it can be used in both the title
  // and the calculation in the table
  const priceQuery = useQueryBundle(GetIexQuote, {
    variables: { ticker: iexSymbol || tickerSymbol },
    fetchPolicy: "network-only",
  });

  // const [tradeType, setTradeType] = useState<"buy" | "sell">("buy");

  const tradeType = props.state.currentTradeType;
  const setTradeType = (type: "buy" | "sell") => {
    props.dispatch({ type: "setTradeType", payload: { tradeType: type } });
  };

  const [isLoading, setIsLoading] = useState(false);
  const [buttonIsShaking, setButtonIsShaking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [currentOrderType, setCurrentOrderType] = useState<OrderType>(
    (props.state.order?.type as OrderType) || "market"
  );

  const { BuildOrder, Component } = GetBundleForOrderType(currentOrderType);

  const form = useForm<Partial<EquityTradeInputs>>({
    defaultValues: { duration: "day", ...props.state.order },
  });
  const quantity = form.watch("quantity") || 0;

  const [previewTrade] = useMutationBundle(PreviewTrade);
  const { dispatch } = props;

  const formSubmit = React.useCallback(
    (data: Partial<EquityTradeInputs>) => {
      const order = {
        ...BuildOrder(data),
        quantity: Number(data.quantity),
        price: data.price ? Number(data.price) : undefined,
        stop: data.stop ? Number(data.stop) : undefined,
        symbol: tickerSymbol,
        clientOrderId: props.state.clientOrderId,
        side: tradeType,
      };

      setIsLoading(true);

      previewTrade({
        variables: {
          institutionAccountId: institutionAccountId,
          options: order,
        },
      }).then((r) => {
        setIsLoading(false);
        const preview = r.data?.previewTrade;

        if (!preview) {
          throw new Error("Preview is missing from response");
        }

        if (!preview.__typename) {
          throw new Error("Preview is missing typename");
        }

        if (preview.__typename === "EquityTradeFaultError") {
          throw new Error(`FAULT - ${preview.faultError}`);
        }

        if (preview.__typename === "EquityTradePreviewError") {
          setButtonIsShaking(true);
          setErrorMessage(preview.errorList.join("\n"));
          setTimeout(() => setButtonIsShaking(false), 600);
          return;
        }

        if (preview.__typename === "EquityTradePreview") {
          dispatch({
            type: "previewTrade",
            payload: {
              order,
              preview: {
                ...preview,
                side: tradeType,
              },
            },
          });
          return;
        }

        throw new Error("Preview format not recognized");
      });
    },
    [
      BuildOrder,
      institutionAccountId,
      tickerSymbol,
      previewTrade,
      dispatch,
      tradeType,
    ]
  );

  const price =
    (priceQuery.state === "DONE" && priceQuery.data.iex.quote?.latestPrice) ||
    0;

  return (
    <div data-cy="trade-stock-screen">
      <TradeTitle
        companyName={props.state.companyName}
        priceQuery={priceQuery}
        tickerSymbol={iexSymbol || tickerSymbol}
      />

      <div className={styles.tradeTypeWrapper}>
        <p
          onClick={() => setTradeType("buy")}
          className={classNames(
            styles.tradeTypeText,
            tradeType === "buy" ? styles.textActive : undefined
          )}
        >
          Buy {tickerSymbol}
        </p>
        <p
          onClick={() => setTradeType("sell")}
          className={classNames(
            styles.tradeTypeText,
            tradeType === "sell" ? styles.textActive : undefined
          )}
        >
          Sell {tickerSymbol}
        </p>
      </div>
      <div className={styles.tradingDivider} />

      <form
        className={styles.tradingForm}
        onSubmit={form.handleSubmit(formSubmit)}
      >
        <DropDownSelect
          name="orderType"
          label="Order Type"
          register={form.register}
          defaultValue={currentOrderType as any}
          options={TradeOptions}
          displayOptions={{ inline: true, size: "slim" }}
          onChange={(option) => {
            // TODO: This option being returned is a string, the
            // type defined said 'Option'. Possible bug.
            const orderType = option as any;
            setCurrentOrderType(orderType.target.value);
          }}
        />

        <FormInput
          type="number"
          name="quantity"
          label="Shares"
          min={0}
          placeholder="0"
          required={true}
          ref={form.register}
          error={form.errors.quantity && form.errors.quantity.message}
          displayOptions={{ inline: true, hideRequiredIndicator: true }}
        />

        <Component form={form} order={props.state.order} />

        <SmallDataTable
          data={[
            ["Market Price", formatCurrency(price)],
            ["Estimated Cost", formatCurrency(quantity * price)],
            [
              "Account",
              `${props.state.brokerName}  • • • • ${props.state.accountNumber}`,
            ],
          ]}
          displayOptions={{ isTwoColumnKeyValue: true }}
        />

        <div className={styles.buyingPowerSection}>
          <p className={styles.buyingPowerText}>
            {props.state.currentTradeType === "buy"
              ? formatCurrency(props.state.buyingPower || 0)
              : props.state.availableShares || 0}
          </p>
          <p className={styles.buyingPowerSubText}>
            {props.state.currentTradeType === "buy"
              ? "Available Buying Power"
              : "Available Shares"}
          </p>
        </div>

        {errorMessage && <p>{errorMessage}</p>}
        <Button data-cy="submit-trade" submit>
          Review
        </Button>
      </form>
      <DisclaimerText>
        <p className={styles.termsText}>
          By clicking Review, you are agreeing to {props.state.brokerName} to
          facilitate a message to complete this trasaction. All transactions are
          executed via a secure connection with {props.state.brokerName}, your
          brokerage account, not TiiCKER.
        </p>
      </DisclaimerText>
      <BrokerConnectionMessage brokerName={props.state.brokerName || ""} />
    </div>
  );
}
